import { Button, Space, Tag, Tooltip, Typography } from "antd";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { DATE_FORMAT_TABLE_SHORT } from "@/constants/date";
import { getExpenseTypeColor } from "@/pages/Expenses/RecordExpense/utils/expenseDrafts";
import { formatCurrency } from "@/utils/currency";
import { ExpenseDraft } from "@/pages/Expenses/RecordExpense/types/expenseDraft";
import { formatQuantity } from "@/utils/numbers";
import { red } from "@ant-design/colors";
import { ColumnsProps } from "@/components/table";
import { ColumnsType } from "antd/es/table";

export const getColumns = ({
  record,
  onEdit,
  onDelete,
  filterOptions,
  filterValues,
  getSortOrder,
}: ColumnsProps<ExpenseDraft>): ColumnsType<ExpenseDraft> => [
  {
    title: "Date",
    dataIndex: "date",
    key: "date",
    hidden: !record?.some((item) => item.date) && !filterValues?.date,
    sorter: true,
    sortOrder: getSortOrder?.("date"),
    render: (date: string) =>
      dayjs(date).isValid() ? dayjs(date).format(DATE_FORMAT_TABLE_SHORT) : "-",
  },
  {
    title: "Type",
    dataIndex: ["expense_type", "name"],
    key: "expense_type_id",
    sorter: true,
    sortOrder: getSortOrder?.("expense_type.name"),
    filters: filterOptions?.expense_type_id,
    filterSearch: true,
    filteredValue: filterValues?.expense_type_id || null,
    render: (_: string, record: ExpenseDraft) => {
      if (!record.expense_type) {
        return "-";
      }

      const color = getExpenseTypeColor(record.expense_type.name);
      const isActive = record.expense_type?.is_active;

      const marker = !isActive ? (
        <Typography.Text style={{ color: red[6] }} strong>
          INACTIVE
        </Typography.Text>
      ) : null;

      return (
        <>
          {marker} <Tag color={color}>{record.expense_type?.name}</Tag>
        </>
      );
    },
  },
  {
    title: "Description",
    dataIndex: ["description", "name"],
    key: "description_id",
    hidden:
      !record?.some((item) => item.description) &&
      !filterValues?.description_id,
    sorter: true,
    sortOrder: getSortOrder?.("description.name"),
    filters: filterOptions?.description_id,
    filterSearch: true,
    filteredValue: filterValues?.description_id || null,
    render: (_: string, record: ExpenseDraft) => {
      if (!record.description) {
        return "-";
      }

      const isActive = record.description.is_active;
      const marker = !isActive ? (
        <Typography.Text style={{ color: red[6] }} strong>
          INACTIVE
        </Typography.Text>
      ) : null;

      return (
        <>
          {marker} {record.description.name}
        </>
      );
    },
  },
  {
    title: "Price",
    dataIndex: "price",
    key: "price",
    hidden: !record?.some((item) => item.price) && !filterValues?.price,
    sorter: true,
    sortOrder: getSortOrder?.("price"),
    render: (price: number) => (price ? formatCurrency(price) : "-"),
  },
  {
    title: "Quantity",
    dataIndex: "quantity",
    key: "quantity",
    hidden: !record?.some((item) => item.quantity) && !filterValues?.quantity,
    sorter: true,
    sortOrder: getSortOrder?.("quantity"),
    render: (quantity: number) =>
      quantity ? formatQuantity(quantity, 2) : "-",
  },
  {
    title: "Unit",
    dataIndex: ["unit", "name"],
    key: "unit_id",
    hidden: !record?.some((item) => item.unit) && !filterValues?.unit_id,
    sorter: true,
    sortOrder: getSortOrder?.("unit.name"),
    filters: filterOptions?.unit_id,
    filterSearch: true,
    filteredValue: filterValues?.unit_id || null,
    render: (_: string, record: ExpenseDraft) => {
      if (!record.unit) {
        return "-";
      }

      const isActive = record.unit.is_active;
      const marker = !isActive ? (
        <Typography.Text style={{ color: red[6] }} strong>
          INACTIVE
        </Typography.Text>
      ) : null;

      return (
        <>
          {marker} {record.unit.name}
        </>
      );
    },
  },
  {
    title: "Supplier",
    dataIndex: ["supplier", "name"],
    key: "supplier_id",
    hidden:
      !record?.some((item) => item.supplier) && !filterValues?.supplier_id,
    sorter: true,
    sortOrder: getSortOrder?.("supplier.name"),
    filters: filterOptions?.supplier_id,
    filterSearch: true,
    filteredValue: filterValues?.supplier_id || null,
    render: (_: string, record: ExpenseDraft) => {
      if (!record.supplier) {
        return "-";
      }

      const isActive = record.supplier.is_active;
      const marker = !isActive ? (
        <Typography.Text style={{ color: red[6] }} strong>
          INACTIVE
        </Typography.Text>
      ) : null;

      return (
        <>
          {marker} {record.supplier.name}
        </>
      );
    },
  },
  {
    title: "Actions",
    key: "actions",
    width: 120,
    fixed: "right" as const,
    render: (_: any, record: ExpenseDraft) => (
      <Space size="small">
        <Tooltip title="Edit">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => onEdit?.(record)}
            size="small"
          />
        </Tooltip>
        <Tooltip title="Remove">
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => onDelete?.(record)}
            size="small"
          />
        </Tooltip>
      </Space>
    ),
  },
];
